import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useSolutionOrg } from '../config/useSolutionOrg';
import {
  selectMemberSession,
  selectOptimizelyClientStatus,
} from '../store/slices/onboardingSlice';
import {
  ResponseError, getMemberInformation, enrollMember,
} from '../store/thunks/onboardingThunk';
import {
  HOME_DEPOT_SETUP_WEB,
  ONBOARDING_BASE_ROUTE,
  PARTNERS_ROUTE,
} from '../routes/namedRoutes';
import { useCustomEventTracking } from './useCustomEventTracking';
import { EverProEdgePublicSolutionOrgName, ServiceNationSolutionOrgName } from './constants';
import { EnrollmentRequest, toEnrolledMember } from './models/EnrollmentRequest';
import {
  getOnboardingFlowTestDecision,
  isOnboardingFlowTestEligibleSolutionOrg,
} from './hooks/useOnboardingFlowTest';
import { isUserInitiatedHomeDepotEnrollment } from './homeDepotEnrollmentIntent';
import { getDashboardEntryRoute } from './dashboardEntryRoute';

interface useCompanyEnrollmentInfoProps {
  calledFromOnboardingComponent: boolean;
  redirectPath?: string;
  hasExplicitRedirectPath?: boolean;
}

/*
* This hook handle the redirection after a user logs in.
* It redirects them to the appropriate page based on their enrollment status
* and whether they have answered the business form questions.
*/
export function useCompanyEnrollmentInfoForLogin({
  calledFromOnboardingComponent,
  hasExplicitRedirectPath = false,
  redirectPath,
}: useCompanyEnrollmentInfoProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { trackPartnerEnrollment, trackRequest } = useCustomEventTracking();
  const location = useLocation();
  const { solutionOrgName, envSolutionOrgId } = useSolutionOrg();
  const memberSession = useAppSelector(selectMemberSession);
  const optimizelyClientStatus = useAppSelector(selectOptimizelyClientStatus);

  const getCompanyEnrollmentInfo = useCallback(() => {
    if (optimizelyClientStatus === 'pending') {
      return;
    }

    dispatch(
      getMemberInformation({ trackRequest }),
    )
      .then(unwrapResult)
      .then((getMemberInformationResponse) => {
        const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
          state: location.state,
          search: location.search,
          redirectPath,
        });
        const flowTestDecision = getOnboardingFlowTestDecision({
          memberEmail: getMemberInformationResponse.email || '',
          memberId: getMemberInformationResponse.id,
          optimizelyClientStatus,
          solutionOrgName: solutionOrgName as string,
        });

        const isEligibleSolutionOrg =
          isOnboardingFlowTestEligibleSolutionOrg(solutionOrgName as string);
        const shouldSkipOnboarding =
          isEligibleSolutionOrg &&
          flowTestDecision.shouldSkipOnboarding;
        const dashboardEntryRoute = getDashboardEntryRoute(solutionOrgName);
        const postLoginRedirectPath =
          hasExplicitRedirectPath ? (redirectPath || dashboardEntryRoute) : dashboardEntryRoute;
        const onboardingFlowTestRedirectPath =
          hasExplicitRedirectPath ? (redirectPath || dashboardEntryRoute) : PARTNERS_ROUTE;
        const userInitiatedHdEnrollmentRedirectPath = redirectPath || HOME_DEPOT_SETUP_WEB;

        // Check if member already enrolled in edge
        if (getMemberInformationResponse?.edgeEnrollmentDate) {
          // Home Depot setup is only shown to new members at the end of Edge onboarding, so
          // returning members go straight to their destination (they enroll from partners).
          if (shouldSkipOnboarding && !userInitiatedHdEnrollment) {
            navigate(onboardingFlowTestRedirectPath);
          } else {
            navigate(postLoginRedirectPath);
          }
          // user's company has not yet enrolled in reward
          // or the user is a web user and hasn't answered the business form questions
        } else if (!calledFromOnboardingComponent) {
          if (solutionOrgName === ServiceNationSolutionOrgName ||
            solutionOrgName === EverProEdgePublicSolutionOrgName) {
            navigate(redirectPath || dashboardEntryRoute);
          } else if (shouldSkipOnboarding) {
            // Optimizely test group: skip onboarding and auto-enroll, then honor login target.
            const solutionOrgId = memberSession.solutionOrgId ||
              (envSolutionOrgId as string) || '';
            const request: EnrollmentRequest = {
              memberId: getMemberInformationResponse.id,
              member: toEnrolledMember(getMemberInformationResponse),
              forms: [],
              partners: [],
            };
            dispatch(enrollMember({ solutionOrgId, request, trackRequest }))
              .then(unwrapResult)
              .then(() => {
                trackPartnerEnrollment('Submit', 'Form', {
                  enrollmentType: 'New',
                  isOptimizelyEvent: true,
                });
                navigate(
                  userInitiatedHdEnrollment ?
                    userInitiatedHdEnrollmentRedirectPath :
                    onboardingFlowTestRedirectPath,
                );
              })
              .catch((error: ResponseError) => {
                trackRequest('Error', 'Post', 'EnrollMember', {
                  error,
                  apiResponseErrorStatus: error.apiResponseErrorStatus,
                });
                navigate(ONBOARDING_BASE_ROUTE);
              });
          } else {
            navigate(ONBOARDING_BASE_ROUTE);
          }
        }
      })
      .catch((error: ResponseError) => {
        trackRequest('Error', 'Get', 'CompanyEnrollmentInformation', {
          error,
          apiResponseErrorStatus: error.apiResponseErrorStatus,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    solutionOrgName,
    optimizelyClientStatus,
    hasExplicitRedirectPath,
    redirectPath,
    memberSession.solutionOrgId,
    envSolutionOrgId,
    trackPartnerEnrollment,
    trackRequest,
  ]);

  return getCompanyEnrollmentInfo;
}
